import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { gql, useQuery, useMutation, useLazyQuery } from '@apollo/client';
import FileList from './FileList';
import UploadProgressBar from './components/UploadProgressBar';
import { filterAssetData } from './assetUtils';
import useUploadManager from './hooks/useUploadManager';
import { flashMessage as __flashMessage } from '~/app/actions';

type Props = {
  selectedAsset: string;
  selectedAssetUrl: string,
  setSelectedAsset: (string) => void,
  path: string,
  setPath: (path: string) => void,
  inline?: boolean,
  allowUpload?: boolean,
}

export default function FileBrowser(props: Props) {
  const {
    selectedAsset,
    selectedAssetUrl,
    setSelectedAsset,
    path,
    setPath,
    inline = false,
    allowUpload = false,
  } = props;

  const dispatch = useDispatch();
  const showFlashMessage = useCallback((message, type, delay?) => dispatch(__flashMessage(message, type, delay)), [dispatch]);

  const [filesToUpload, setFilesToUpload] = useState([]);

  const { loading: isFetching, error, data } = useQuery(GET_ASSETS, {
    variables: { path },
    fetchPolicy: 'network-only'
  });
  const [addFolderMutation] = useMutation(ADD_FOLDER);
  const [getFolders] = useLazyQuery(GET_FOLDERS, {
    variables: { path },
    fetchPolicy: 'network-only'
  });

  const onError = (message) => {
    showFlashMessage(message, 'ERROR', 8000)
  }

  const {
    uploading,
  } = useUploadManager({
    path,
    files: filesToUpload,
    onError
  });

  const onDropHandler = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length) {
      const fileNames = rejectedFiles.map(f => f.name);
      showFlashMessage(`These file types are not supported:\n${fileNames.join('\n')}`, 'ERROR');
      return;
    }

    setFilesToUpload(acceptedFiles);
  }

  // Handler for adding a folder. Optimistically adds new folder to the file list.
  const addFolder = useCallback(async (name) => {
    if (!name) {
      return;
    }

    const optimisticFolder = {
      Name: `${path || ''}${name}/`,
      __typename: 'Folder'
    };

    try {
      await addFolderMutation({
        variables: {
          name,
          path
        },
        optimisticResponse: {
          createAssetFolder: optimisticFolder
        },
      });
      await getFolders({
        variables: {
          path
        }
      });
    } catch (e) {
      console.error('Failed to add folder', e);
    }
  }, [addFolderMutation, getFolders, path]);

  const navigateUp = () => {
    // Remove last path segment and append '/' if not at root level
    // Ex: 'assets/images/' => [ 'assets', 'images', '' ] => 'assets/'
    // Ex: 'assets/' => [ 'assets', '' ] => ''
    let newPath = path.split('/').slice(0, -2).join('/');

    newPath = newPath.length ? newPath.concat('/') : newPath;

    setPath(newPath);
    setSelectedAsset('');
  };
  const navigateDown = (folder) => {
    setPath(path.concat(folder));
    setSelectedAsset('');
  };

  if (error) {
    return (
      <FileList
        isFetching={isFetching}
        error={error?.graphQLErrors?.[0]?.message}
        path={path}
        navigateUp={navigateUp}
        inline={inline}
      />
    );
  }

  const { assets, folders } = filterAssetData(data, path);

  return (
    <>
      <UploadProgressBar isUploading={uploading} />
      <FileList
        isFetching={isFetching}
        path={path}
        navigateUp={navigateUp}
        navigateDown={navigateDown}
        selectedAsset={selectedAsset}
        selectedAssetUrl={selectedAssetUrl}
        setSelectedAsset={setSelectedAsset}
        assets={assets}
        folders={folders}
        inline={inline}
        allowUpload={allowUpload}
        uploading={uploading}
        onDropHandler={onDropHandler}
        addFolderHandler={addFolder}
      />
    </>
  );
}

const GET_ASSETS = gql`
  query getAssetsAndFolders($path: String) {
    assets (path: $path) {
      Key
      Name: Key
      Size
      LastModified
    }
    folders (path: $path) {
      Name: Prefix
    }
  }
`;

const GET_FOLDERS = gql`
  query getFolders($path: String) {
    folders (path: $path) {
      Name: Prefix
    }
  }
`;

const ADD_FOLDER = gql`
  mutation createAssetFolder($name: String!, $path: String!) {
    createAssetFolder(name: $name, path: $path) {
      Name: Prefix
    }
  }
`;
